/*
Copyright 2026 Element Creations Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

const AVATAR_COLORS = [
  "#5c6bc0",
  "#26a69a",
  "#ec407a",
  "#ab47bc",
  "#42a5f5",
  "#ffa726",
  "#66bb6a",
  "#ef5350",
];
function avatarColor(name: string): string {
  let hash = 0;
  for (let i = 0; i < name.length; i++)
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

interface Frame {
  width: number;
  height: number;
  roomName: string;
  participantCount: number;
  speakerName: string;
  isSpeaking: boolean;
  isMicEnabled: boolean;
  video: HTMLVideoElement | null;
  screenShare?: boolean;
  pulsePhase: number;
  labels: {
    participantsCount: string;
    speakingNow: string;
    recentlySpoke: string;
    micMuted: string;
    fallbackInitial: string;
  };
}

/** Shared composition for native video PiP, Safari PiP and document PiP. */
export function renderPictureInPictureFrame(
  ctx: CanvasRenderingContext2D,
  frame: Frame,
): void {
  const {
    width,
    height,
    roomName,
    participantCount,
    speakerName,
    isSpeaking,
    isMicEnabled,
    video,
    pulsePhase,
    labels,
  } = frame;
  const hasVideo =
    video !== null && video.videoWidth > 0 && video.videoHeight > 0;
  ctx.fillStyle = "#14161c";
  ctx.fillRect(0, 0, width, height);
  ctx.textBaseline = "alphabetic";

  if (hasVideo) {
    const rotation = frame.screenShare
      ? Number(video.dataset?.presentationRotation ?? 0)
      : 0;
    const rotated = rotation === 90 || rotation === 270;
    const scale = (frame.screenShare ? Math.min : Math.max)(
      width / (rotated ? video.videoHeight : video.videoWidth),
      height / (rotated ? video.videoWidth : video.videoHeight),
    );
    const dw = video.videoWidth * scale,
      dh = video.videoHeight * scale;
    if (rotated) {
      ctx.save();
      ctx.translate(width / 2, height / 2);
      ctx.rotate((rotation * Math.PI) / 180);
      ctx.drawImage(video, -dw / 2, -dh / 2, dw, dh);
      ctx.restore();
    } else ctx.drawImage(video, (width - dw) / 2, (height - dh) / 2, dw, dh);
    const top = ctx.createLinearGradient(0, 0, 0, 85);
    top.addColorStop(0, "rgba(0, 0, 0, 0.8)");
    top.addColorStop(1, "rgba(0, 0, 0, 0)");
    ctx.fillStyle = top;
    ctx.fillRect(0, 0, width, 85);
    const bottom = ctx.createLinearGradient(0, height - 90, 0, height);
    bottom.addColorStop(0, "rgba(0, 0, 0, 0)");
    bottom.addColorStop(1, "rgba(0, 0, 0, 0.85)");
    ctx.fillStyle = bottom;
    ctx.fillRect(0, height - 90, width, 90);
  } else {
    ctx.fillStyle = "#1f222b";
    ctx.beginPath();
    ctx.roundRect(20, 20, width - 40, height - 40, 20);
    ctx.fill();
    ctx.strokeStyle = isSpeaking ? "#34c759" : "rgba(255, 255, 255, 0.08)";
    ctx.lineWidth = isSpeaking ? 4 : 2;
    ctx.stroke();

    const centerX = width / 2,
      avatarY = 140,
      avatarRadius = 54;
    if (isSpeaking) {
      ctx.beginPath();
      ctx.arc(
        centerX,
        avatarY,
        avatarRadius + 10 + Math.sin(pulsePhase) * 6,
        0,
        Math.PI * 2,
      );
      ctx.fillStyle = "rgba(52, 199, 89, 0.28)";
      ctx.fill();
      ctx.beginPath();
      ctx.arc(centerX, avatarY, avatarRadius + 4, 0, Math.PI * 2);
      ctx.strokeStyle = "#34c759";
      ctx.lineWidth = 3.5;
      ctx.stroke();
    }
    ctx.beginPath();
    ctx.arc(centerX, avatarY, avatarRadius, 0, Math.PI * 2);
    ctx.fillStyle = avatarColor(speakerName);
    ctx.fill();
    ctx.fillStyle = "#ffffff";
    ctx.font = "700 46px -apple-system, BlinkMacSystemFont, sans-serif";
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    ctx.fillText(
      (speakerName.trim()[0] || labels.fallbackInitial).toUpperCase(),
      centerX,
      avatarY + 2,
    );
    ctx.textBaseline = "alphabetic";
  }

  // Metadata is drawn for both camera video and the avatar, on every frame.
  ctx.fillStyle = "rgba(255, 255, 255, 0.85)";
  ctx.font = "500 18px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.textAlign = "left";
  const count = `${labels.participantsCount}: ${participantCount}`;
  ctx.fillText(count, 40, 50);
  ctx.textAlign = "right";
  ctx.fillText(
    roomName.length > 30 ? roomName.slice(0, 30) + "…" : roomName,
    width - 40,
    50,
    width * 0.54,
  );

  ctx.fillStyle = "#ffffff";
  ctx.font = "600 28px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.textAlign = hasVideo ? "left" : "center";
  const title =
    speakerName.length > 24 ? speakerName.slice(0, 24) + "…" : speakerName;
  ctx.fillText(
    title,
    hasVideo ? 24 : width / 2,
    hasVideo ? height - 48 : 245,
    width - 48,
  );
  ctx.fillStyle = isSpeaking
    ? "#34c759"
    : !isMicEnabled
      ? "#ff6b6b"
      : "#9ea4b0";
  ctx.font = "500 18px -apple-system, BlinkMacSystemFont, sans-serif";
  ctx.fillText(
    isSpeaking
      ? labels.speakingNow
      : !isMicEnabled
        ? labels.micMuted
        : labels.recentlySpoke,
    hasVideo ? 24 : width / 2,
    hasVideo ? height - 20 : 280,
  );
  if (hasVideo && isSpeaking) {
    ctx.strokeStyle = "#34c759";
    ctx.lineWidth = 6;
    ctx.strokeRect(3, 3, width - 6, height - 6);
  }
}
